<template>
  <div class="extraction-template-field-item" :class="styleClass">
    <div class="etfi-header-row">
      <v-text-field
        v-if="isActive"
        autofocus
        class="etfi-label text-body-2"
        label="Name"
        :disabled="isDeleted"
        :value="fieldSettings.title"
        :error-messages="titleErrorMessage"
        @change="onUpdateTitle"
        @click.stop="() => {}"
      />
      <div>
        <extraction-template-item-actions
          :item-style="itemStyle"
          @delete="$emit('delete-field')"
          @revert="$emit('revert-field')"
        />
      </div>
    </div>
    <v-textarea
      class="etfi-description text-body-2 mt-5"
      label="Description (Optional)"
      rows="1"
      auto-grow
      dense
      hide-details
      :disabled="isDeleted"
      :value="fieldSettings.description"
      @change="onUpdateDescription"
    />
    <v-tooltip right :disabled="!lockType" open-delay="500">
      <template #activator="{ on, attrs }">
        <div v-bind="attrs" v-on="on">
          <v-select
            class="etfi-select mt-6"
            dense
            filled
            rounded
            return-object
            :disabled="lockType || isDeleted"
            :error-messages="typeErrorMessage"
            :items="selectItems"
            :value="initialSelection"
            item-value="label"
            @input="onTypeSelection"
          >
            <template #selection="{ item }">
              <v-icon class="mr-2" small>{{ item.icon }}</v-icon>
              <span>{{ item.label }}</span>
            </template>
            <template #item="{ item }">
              <v-icon class="mr-2" small>{{ item.icon }}</v-icon>
              <span>{{ item.label }}</span>
            </template>
          </v-select>
        </div>
      </template>
      <span>Cannot modify the type of published fields</span>
    </v-tooltip>
    <extraction-template-option-list
      v-if="showOptionList"
      :disabled="isDeleted"
      :options="fieldSettings.multipleChoiceOptions"
      :allow-other="fieldSettings.otherOption"
      :data-type="typeName"
      @input-options="onUpdateOptions"
      @input-allowOther="onUpdateAllowOther"
    />
    <div class="etfi-close-row mb-5">
      <action-button @click="$emit('close')"> Close </action-button>
    </div>
  </div>
</template>

<script>
import {
  DataExtractionFieldBase,
  DataExtractionFieldText,
  DataExtractionFieldMultipleSelect,
  DataExtractionFieldSingleSelect,
} from '@/models';
import ActionButton from '@/components/Common/ActionButton.vue';
import ExtractionTemplateOptionList from './ExtractionTemplateOptionList.vue';
import { getIconForType } from '@/modules/dataExtractionFieldHelpers';
import ExtractionTemplateItemActions from './ExtractionTemplateItemActions.vue';
import { ExtractionTemplateItemStyle } from '@/enums';

export default {
  name: 'ExtractionTemplateFieldItem',
  components: {
    ActionButton,
    ExtractionTemplateOptionList,
    ExtractionTemplateItemActions,
  },
  props: {
    value: {
      type: DataExtractionFieldBase,
      required: true,
    },
    itemStyle: {
      type: String,
      default: ExtractionTemplateItemStyle.DEFAULT,
      validator: (val) =>
        Object.values(ExtractionTemplateItemStyle).includes(val),
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    lockType: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'input', 'delete-field', 'revert-field'],
  data: () => ({
    fieldSettings: {},
    fieldType: DataExtractionFieldBase,
  }),
  computed: {
    selectItems() {
      return [
        {
          label: 'Text',
          type: DataExtractionFieldText,
          icon: getIconForType('DataExtractionFieldText'),
        },
        {
          label: 'Select One',
          type: DataExtractionFieldSingleSelect,
          icon: getIconForType('DataExtractionFieldSingleSelect'),
        },
        {
          label: 'Select Multiple',
          type: DataExtractionFieldMultipleSelect,
          icon: getIconForType('DataExtractionFieldMultipleSelect'),
        },
      ];
    },
    initialSelection() {
      for (const item of this.selectItems) {
        if (this.value instanceof item.type) {
          return item;
        }
      }
      return this.selectItems[0];
    },
    typeName() {
      return this.fieldType.name;
    },
    showOptionList() {
      switch (this.typeName) {
        case 'DataExtractionFieldSingleSelect':
        case 'DataExtractionFieldMultipleSelect':
          return true;
      }
      return false;
    },
    titleErrorMessage() {
      if (
        this.fieldSettings.title.length === 0 &&
        this.value.title.length > 0
      ) {
        return 'Title cannot be empty';
      }
      return '';
    },
    typeErrorMessage() {
      if (this.showOptionList) {
        if (this.fieldSettings.multipleChoiceOptions.length === 0) {
          return 'At least one option is required';
        }
      }
      return '';
    },
    isDeleted() {
      return this.itemStyle === ExtractionTemplateItemStyle.DELETED;
    },
    styleClass() {
      return `style-${this.itemStyle.toLowerCase()}`;
    },
  },
  watch: {
    itemStyle(newStyle) {
      if (newStyle === ExtractionTemplateItemStyle.DEFAULT) {
        this.setFieldDefaultValues();
      }
    },
    value(oldValue, newValue) {
      if (oldValue.id !== newValue.id) {
        this.setFieldDefaultValues();
      }
    },
  },
  created() {
    this.setFieldDefaultValues();
  },
  methods: {
    getIconForType: getIconForType,
    setFieldDefaultValues() {
      this.fieldSettings = { ...this.value };
      this.fieldType = this.value.constructor;
    },
    emitInput() {
      const newField = new this.fieldType(this.fieldSettings);
      this.fieldSettings = { ...newField, ...this.fieldSettings };
      if (this.titleErrorMessage || this.typeErrorMessage) {
        return;
      }
      this.$emit('input', newField);
    },
    onTypeSelection(selection) {
      this.fieldType = selection.type;
      if (
        this.showOptionList &&
        !this.fieldSettings.multipleChoiceOptions
      ) {
        this.fieldSettings.multipleChoiceOptions = [
          'Option 1',
          'Option 2',
        ];
      }

      this.emitInput();
    },
    onUpdateOptions(options) {
      this.fieldSettings.multipleChoiceOptions = options;
      this.emitInput();
    },
    onUpdateAllowOther(otherOption) {
      this.fieldSettings.otherOption = otherOption;
      this.emitInput();
    },
    onUpdateTitle(title) {
      this.fieldSettings.title = title;
      this.emitInput();
    },
    onUpdateDescription(description) {
      this.fieldSettings.description = description;
      this.emitInput();
    },
  },
};
</script>

<style scoped lang="scss">
.extraction-template-field-item {
  display: grid;
  justify-items: start;

  border: 2px solid;
  border-color: rgba(0, 0, 0, 0);
  border-radius: inherit;

  &.style-new {
    border-color: var(--v-primary-base);
  }

  &.style-modified {
    border-color: var(--v-success-base);
  }

  &.style-deleted {
    border-color: var(--v-error-base);
  }
}

.etfi-header-row {
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  width: 100%;
}

.etfi-label {
  min-width: 20rem;
}

.etfi-description {
  min-width: 20rem;
}

.etfi-select {
  ::v-deep input {
    width: 0;
  }
}

.etfi-close-row {
  justify-self: end;
}
</style>
